<app-dashboard-shell
  title="OTR Ops Readiness"
  description="Long-distance / over-the-road operations readiness checklist."
  [lastReload]="lastReload"
  [loading]="loading"
  (refresh)="refresh()"
>
  <p class="p-mb-1 p-md-sm-0" filters>
    <span *ngIf="loading">Loading... </span>
    <span *ngIf="!loading && dayGroups.length">
      {{ totalEvents }} event{{ totalEvents !== 1 ? 's' : '' }}
    </span>
  </p>

  <div class="p-col-12">
    <p-blockUI [blocked]="loading" [autoZIndex]="false">
      <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
    </p-blockUI>

    <div *ngIf="!loading && dayGroups.length === 0" class="dashboard-card p-text-center p-p-4">
      <p>No booked, confirmed, or completed events in the selected period.</p>
    </div>

    <div *ngIf="!loading && dayGroups.length" class="otr-ops-accordion-wrapper">
      <div class="otr-ops-accordion-col-headers">
        <span class="otr-ops-accordion-date">Date</span>
        <span class="otr-ops-accordion-count">Events</span>
        <span class="otr-ops-accordion-days">Days Out</span>
      </div>
      <div *ngFor="let day of dayGroups" class="otr-ops-day-group">
        <div class="otr-ops-day-group__header"
             role="button"
             tabindex="0"
             [attr.aria-expanded]="isDayExpanded(day.date)"
             (click)="toggleDayExpanded(day.date)"
             (keydown.enter)="toggleDayExpanded(day.date)"
             (keydown.space)="toggleDayExpanded(day.date); $event.preventDefault()"
        >
          <div class="otr-ops-accordion-header">
            <i class="pi" [ngClass]="isDayExpanded(day.date) ? 'pi-chevron-down' : 'pi-chevron-right'" style="margin-right: 0.4rem;"></i>
            <span class="otr-ops-accordion-date">{{ day.displayDate }}</span>
            <span class="otr-ops-accordion-count">{{ day.eventCount }}</span>
            <span class="otr-ops-accordion-days">
              {{ day.daysUntilMove !== null ? day.daysUntilMove + ' day' + (day.daysUntilMove !== 1 ? 's' : '') : '\u2014' }}
              <i *ngIf="isDayReady(day)" class="pi pi-check-circle otr-ops-day-ready" role="img" aria-label="All items ready"></i>
              <i *ngIf="!isDayReady(day)" class="pi pi-times-circle otr-ops-day-not-ready" role="img" aria-label="Items outstanding"></i>
            </span>
          </div>
        </div>
        <div *ngIf="isDayExpanded(day.date)" class="otr-ops-day-group__content">
          <div class="otr-ops-events-table-container" tabindex="0" role="region" aria-label="OTR events for {{ day.displayDate }}">
            <table class="otr-ops-events-table">
              <caption class="sr-only">OTR readiness events for {{ day.displayDate }}</caption>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>FP Notification<br>Status</th>
                  <th>Customer</th>
                  <th>Executing<br>Franchise</th>
                  <th>Sales Agent</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Customer Packet<br>Signed</th>
                  <th>Visual Inventory<br>Signed</th>
                  <th>Paid<br>Total</th>
                  <th>Job Shared<br>on MR</th>
                  <th>Scope<br>Confirmed</th>
                  <th>BOL Signed<br>Origin</th>
                  <th>Inventory<br>Checklist</th>
                  <th>Itinerary<br>Created</th>
                  <th>Rental<br>Reserved</th>
                  <th>EOM<br>Checklist</th>
                  <th>10-Day<br>Call</th>
                  <th>7-Day<br>Call</th>
                  <th>Driver DL<br>on File</th>
                  <th>DOT Medical<br>Card on File</th>
                  <th>1099/Contracts<br>Completed</th>
                  <th>High-Value<br>Declared</th>
                  <th>Job<br>Total</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let evt of day.events" [class.otr-ops-row-overdue]="hasOverdueItems(evt)">
                  <td>
                    <span *ngIf="hasOverdueItems(evt)" class="otr-ops-overdue-icon" role="img" aria-label="Overdue" pTooltip="Has overdue items" tooltipPosition="left"><i class="pi pi-exclamation-triangle"></i></span>
                    <a *ngIf="evt.jobId" class="job-code-link" href="javascript:void(0)" (click)="openJobDetails($event, evt)" (dblclick)="openJobNewTab(evt)">{{ evt.jobCode }}</a><span *ngIf="!evt.jobId">{{ evt.jobCode }}</span>
                  </td>
                  <td class="otr-ops-dropdown-cell">
                    <p-dropdown
                      [options]="fpNotificationOptions"
                      optionLabel="label"
                      optionValue="value"
                      [ngModel]="evt.fpNotificationStatus"
                      (ngModelChange)="setJobField(evt, 'fpNotificationStatus', $event, 'otrFpNotificationStatus')"
                      placeholder="—"
                      appendTo="body"
                      styleClass="otr-ops-dropdown"
                      ariaLabel="FP Notification Status"
                    >
                      <ng-template pTemplate="selectedItem" let-option>
                        <span *ngIf="option?.value" class="otr-ops-fp-status otr-ops-fp-status-{{ option.color }}">{{ option.label }}</span>
                        <span *ngIf="!option?.value" class="otr-ops-dropdown-empty">—</span>
                      </ng-template>
                      <ng-template pTemplate="item" let-option>
                        <span *ngIf="option.value" class="otr-ops-fp-status otr-ops-fp-status-{{ option.color }}">{{ option.label }}</span>
                        <span *ngIf="!option.value" class="otr-ops-dropdown-blank"></span>
                      </ng-template>
                    </p-dropdown>
                  </td>
                  <td class="otr-ops-customer-cell">{{ evt.customer }}</td>
                  <td class="otr-ops-dropdown-cell">
                    <p-dropdown
                      [options]="executingFranchiseOptions"
                      optionLabel="label"
                      optionValue="value"
                      [ngModel]="evt.executingFranchise"
                      (ngModelChange)="setJobField(evt, 'executingFranchise', $event, 'otrExecutingFranchise')"
                      placeholder="—"
                      appendTo="body"
                      styleClass="otr-ops-dropdown"
                      ariaLabel="Executing Franchise"
                    >
                      <ng-template pTemplate="selectedItem" let-option>
                        <span *ngIf="option?.value">{{ option.label }}</span>
                        <span *ngIf="!option?.value" class="otr-ops-dropdown-empty">—</span>
                      </ng-template>
                      <ng-template pTemplate="item" let-option>
                        <span *ngIf="option.value">{{ option.label }}</span>
                        <span *ngIf="!option.value" class="otr-ops-dropdown-blank"></span>
                      </ng-template>
                    </p-dropdown>
                  </td>
                  <td>{{ evt.salesAgent }}</td>
                  <td><span class="otr-ops-status otr-ops-status-{{ evt.eventStatus | lowercase }}">{{ evt.eventStatus }}</span></td>
                  <td>{{ evt.eventType }}</td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'customerPacketSigned')"><i *ngIf="evt.customerPacketSigned" class="pi pi-check otr-ops-check-yes" role="img" aria-label="Signed"></i><i *ngIf="!evt.customerPacketSigned" class="pi pi-times otr-ops-check-no" role="img" aria-label="Not signed"></i></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'visualInventorySigned')"><p-checkbox [(ngModel)]="evt.visualInventorySigned" [binary]="true" (onChange)="toggleCheckbox(evt, 'visualInventorySigned', evt.visualInventorySigned)" ariaLabel="Visual Inventory Signed"></p-checkbox></td>
                  <td class="otr-ops-paid-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'jobPaidTotal')">{{ evt.jobPaidTotal === null || evt.jobPaidTotal === undefined ? '\u2014' : evt.jobPaidTotal }}</td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'jobShared')"><p-checkbox [(ngModel)]="evt.jobShared" [binary]="true" (onChange)="toggleCheckbox(evt, 'jobShared', evt.jobShared)" ariaLabel="Job Shared"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'scopeConfirmed')"><p-checkbox [(ngModel)]="evt.scopeConfirmed" [binary]="true" (onChange)="toggleCheckbox(evt, 'scopeConfirmed', evt.scopeConfirmed, 'scopesConfirmed')" ariaLabel="Scope Confirmed"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'bolOriginSigned')"><i *ngIf="evt.bolOriginSigned" class="pi pi-check otr-ops-check-yes" role="img" aria-label="Signed"></i><i *ngIf="!evt.bolOriginSigned" class="pi pi-times otr-ops-check-no" role="img" aria-label="Not signed"></i></td>
                  <td class="otr-ops-check-cell"><p-checkbox [(ngModel)]="evt.inventoryChecklist" [binary]="true" (onChange)="toggleCheckbox(evt, 'inventoryChecklist', evt.inventoryChecklist)" ariaLabel="Inventory Checklist"></p-checkbox></td>
                  <td class="otr-ops-check-cell"><p-checkbox [(ngModel)]="evt.itineraryCreated" [binary]="true" (onChange)="toggleCheckbox(evt, 'itineraryCreated', evt.itineraryCreated)" ariaLabel="Itinerary Created"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'rentalReserved')"><p-checkbox [(ngModel)]="evt.rentalReserved" [binary]="true" (onChange)="toggleCheckbox(evt, 'rentalReserved', evt.rentalReserved)" ariaLabel="Rental Reserved"></p-checkbox></td>
                  <td class="otr-ops-check-cell"><i *ngIf="evt.eomChecklistCreated" class="pi pi-check otr-ops-check-yes" role="img" aria-label="Created"></i><i *ngIf="!evt.eomChecklistCreated" class="pi pi-times otr-ops-check-no" role="img" aria-label="Not created"></i></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'tenDayCall')"><p-checkbox [(ngModel)]="evt.tenDayCall" [binary]="true" (onChange)="toggleCheckbox(evt, 'tenDayCall', evt.tenDayCall)" ariaLabel="10-Day Call"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'sevenDayCall')"><p-checkbox [(ngModel)]="evt.sevenDayCall" [binary]="true" (onChange)="toggleCheckbox(evt, 'sevenDayCall', evt.sevenDayCall)" ariaLabel="7-Day Call"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'driverDlOnFile')"><p-checkbox [(ngModel)]="evt.driverDlOnFile" [binary]="true" (onChange)="toggleCheckbox(evt, 'driverDlOnFile', evt.driverDlOnFile)" ariaLabel="Driver DL on File"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'dotMedicalOnFile')"><p-checkbox [(ngModel)]="evt.dotMedicalOnFile" [binary]="true" (onChange)="toggleCheckbox(evt, 'dotMedicalOnFile', evt.dotMedicalOnFile)" ariaLabel="DOT Medical Card on File"></p-checkbox></td>
                  <td class="otr-ops-check-cell" [class.otr-ops-cell-overdue]="isOverdue(evt, 'contractsCompleted')"><p-checkbox [(ngModel)]="evt.contractsCompleted" [binary]="true" (onChange)="toggleCheckbox(evt, 'contractsCompleted', evt.contractsCompleted)" ariaLabel="1099/Contracts Completed"></p-checkbox></td>
                  <td class="otr-ops-check-cell"><p-checkbox [(ngModel)]="evt.highValueDeclared" [binary]="true" (onChange)="toggleCheckbox(evt, 'highValueDeclared', evt.highValueDeclared)" ariaLabel="High-Value Declared"></p-checkbox></td>
                  <td class="otr-ops-total-cell">{{ evt.jobTotal ? evt.jobTotal : '\u2014' }}</td>
                  <td class="otr-ops-notes-cell"><input pInputText type="text" [(ngModel)]="evt.notes" (input)="onNotesInput(evt)" class="otr-ops-notes-input" placeholder="Notes" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</app-dashboard-shell>